import {testIdProps} from '@github-ui/test-id-props'
import {XIcon} from '@primer/octicons-react'
import {Box, Heading, IconButton, Overlay, useFocusTrap} from '@primer/react'
import {useRef} from 'react'

import {useMemexRootHeight} from '../../../../hooks/use-memex-root-height'
import type {ChartState} from '../../../../state-providers/charts/use-charts'
import {useInsightsConfigurationPane} from '../../hooks/use-insights-configuration-pane'
import {ActionButtons} from './action-buttons'
import {LayoutSelector} from './layout-selector'
import {XAxisSelector} from './x-axis-selector'
import {YAxisSelector} from './y-axis-selector'

const InsightsConfigurationPaneForm = ({
  chart,
  showUpsellDialog,
}: {
  chart: ChartState
  showUpsellDialog: () => void
}) => {
  return (
    <div>
      <Box sx={{px: 4}}>
        <LayoutSelector chart={chart} />
        <XAxisSelector chart={chart} />
        <YAxisSelector chart={chart} />
      </Box>
      <Box sx={{p: 4, borderTop: '1px solid', borderColor: 'border.default'}}>
        <ActionButtons chart={chart} showUpsellDialog={showUpsellDialog} />
      </Box>
    </div>
  )
}

export const InsightsConfigurationPane = ({
  chart,
  showUpsellDialog,
}: {
  chart: ChartState
  showUpsellDialog: () => void
}) => {
  // We don't ever set this, but the overlay needs this
  const returnFocusRef = useRef<HTMLElement>(null)
  // We need to pass an initialFocusRef to _both_ overlay and useFocusTrap
  // so create it here and pass it to both
  const initialFocusRef = useRef<HTMLButtonElement>(null)

  const {closePane, isOpen} = useInsightsConfigurationPane()

  if (!isOpen) return null

  return (
    <Overlay
      initialFocusRef={initialFocusRef}
      returnFocusRef={returnFocusRef}
      onEscape={closePane}
      onClickOutside={closePane}
    >
      <InsightsConfigurationPaneContentBase initialFocusRef={initialFocusRef}>
        <Box sx={{px: 4, py: 3, display: 'flex', justifyContent: 'space-between'}}>
          <Heading as="h2" sx={{fontSize: 2}}>
            Configure chart
          </Heading>
          <IconButton
            aria-label="Close configuration pane"
            variant="invisible"
            icon={XIcon}
            sx={{px: 2}}
            onClick={closePane}
            {...testIdProps('side-panel-button-close')}
          />
        </Box>
        <InsightsConfigurationPaneForm chart={chart} showUpsellDialog={showUpsellDialog} />
      </InsightsConfigurationPaneContentBase>
    </Overlay>
  )
}

const InsightsConfigurationPaneContentBase: React.FC<{
  children?: React.ReactNode
  initialFocusRef: React.RefObject<HTMLButtonElement>
}> = ({initialFocusRef, children}) => {
  const appHeight = useMemexRootHeight()

  const {isClosing} = useInsightsConfigurationPane()
  const {containerRef} = useFocusTrap({
    disabled: !!isClosing,
    initialFocusRef,
  })

  return (
    <Box
      ref={containerRef as React.RefObject<HTMLDivElement>}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'fixed',
        bottom: '0',
        right: '0',
        width: 400,
        height: appHeight.clientHeight,
        backgroundColor: 'canvas.default',
        boxShadow: 'shadow.large',
        borderLeft: '1px solid',
        borderColor: 'border.default',
        ':focus': {outline: 'none'},
      }}
      tabIndex={-1}
      {...testIdProps('insights-configuration-pane')}
    >
      {children}
    </Box>
  )
}

try{ InsightsConfigurationPaneForm.displayName ||= 'InsightsConfigurationPaneForm' } catch {}
try{ InsightsConfigurationPane.displayName ||= 'InsightsConfigurationPane' } catch {}